import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lightbulb, ThumbsUp, Loader2, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { SEOHead } from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUS_META = {
  new: { label: 'Nouveau', className: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'En cours', className: 'bg-amber-100 text-amber-700' },
  done: { label: 'Réalisé', className: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Refusé', className: 'bg-gray-100 text-gray-600' },
};

export default function SuggestionsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [voting, setVoting] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/suggestions`, { withCredentials: true });
      setItems(res.data);
    } catch { toast.error('Impossible de charger les suggestions'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/suggestions`, form, { withCredentials: true });
      setItems((prev) => [res.data, ...prev].sort((a, b) => b.votes - a.votes));
      setForm({ title: '', description: '' });
      toast.success("Merci ! Votre idée a été transmise à l'équipe BuildConnect");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de l\'envoi (titre ≥ 5 caractères, description ≥ 10)');
    } finally { setSubmitting(false); }
  };

  const vote = async (id) => {
    setVoting(id);
    try {
      const res = await axios.post(`${API}/suggestions/${id}/vote`, {}, { withCredentials: true });
      setItems((prev) => prev.map((s) => (s.id === id ? res.data : s)).sort((a, b) => b.votes - a.votes));
    } catch { toast.error('Erreur lors du vote'); }
    finally { setVoting(null); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8">
      <SEOHead title="Vos idées pour BuildConnect" path="/dashboard/suggestions" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-overline mb-2">Communauté</p>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-[#111827] mb-2" data-testid="suggestions-title">Vos idées pour BuildConnect</h1>
        <p className="text-[#4B5563] mb-8">Proposez une fonctionnalité et votez « J'aimerais aussi » sur les idées des autres membres : les plus demandées sont développées en priorité.</p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Card className="lg:col-span-2 self-start" data-testid="suggestion-form-card">
            <CardHeader><CardTitle className="font-heading flex items-center gap-2 text-lg"><Lightbulb className="w-5 h-5 text-[#F59E0B]" /> Proposer une fonctionnalité</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="sug-title">Titre</Label>
                  <Input id="sug-title" required minLength={5} maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Export PDF des candidatures" className="input-field" data-testid="suggestion-title-input" />
                </div>
                <div>
                  <Label htmlFor="sug-desc">Description</Label>
                  <Textarea id="sug-desc" required minLength={10} rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Décrivez le besoin, dans quelle situation cela vous aiderait…" className="input-field" data-testid="suggestion-description-input" />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="suggestion-submit">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Envoyer mon idée
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="lg:col-span-3 space-y-3" data-testid="suggestions-list">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
            ) : items.length === 0 ? (
              <Card className="p-10 text-center text-[#6B7280]" data-testid="suggestions-empty">Aucune idée pour le moment — soyez le premier à proposer une fonctionnalité !</Card>
            ) : items.map((s) => {
              const st = STATUS_META[s.status] || STATUS_META.new;
              return (
                <Card key={s.id} className="card-surface" data-testid={`suggestion-${s.id}`}>
                  <CardContent className="p-5 flex gap-4">
                    <button
                      onClick={() => vote(s.id)}
                      disabled={voting === s.id}
                      className={`flex flex-col items-center justify-center w-16 h-16 rounded-xl border-2 transition-colors flex-shrink-0 ${s.voted_by_me ? 'bg-[#1E3A8A] border-[#1E3A8A] text-white' : 'border-[#E5E7EB] text-[#1E3A8A] hover:border-[#1E3A8A]'}`}
                      title={s.voted_by_me ? 'Retirer mon vote' : "J'aimerais aussi"}
                      data-testid={`suggestion-vote-${s.id}`}
                    >
                      <ThumbsUp className={`w-5 h-5 ${s.voted_by_me ? 'fill-white' : ''}`} />
                      <span className="text-lg font-bold leading-none mt-1" data-testid={`suggestion-votes-${s.id}`}>{s.votes}</span>
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <h3 className="font-heading font-bold text-[#111827]">{s.title}</h3>
                        <Badge className={st.className}>{st.label}</Badge>
                      </div>
                      <p className="text-sm text-[#4B5563] mt-1 whitespace-pre-line">{s.description}</p>
                      {s.admin_note && <p className="text-xs text-[#1E3A8A] mt-2 bg-[#1E3A8A]/5 rounded px-2 py-1">Réponse BuildConnect : {s.admin_note}</p>}
                      <p className="text-xs text-[#9CA3AF] mt-2">
                        {s.author_name || 'Un membre'}{s.author_id === user?.id ? ' (vous)' : ''} · {s.created_at ? new Date(s.created_at).toLocaleDateString('fr-FR') : ''}
                        {!s.voted_by_me && <span className="ml-2 text-[#1E3A8A]">← J'aimerais aussi</span>}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
